/* eslint react/prop-types: 0 */

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import FadeIn from "react-fade-in";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { RiArrowDropDownLine, RiArrowDropUpLine } from "react-icons/ri";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import MDModal2 from "components/MDModal2";
// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import TimelineItem from "examples/Timeline/TimelineItem";

// Images
import rawSteak from "assets/food-images/raw-steak.jpg";
import rawSalmon from "assets/food-images/raw-salmon.jpg";
import rawPorkCut from "assets/food-images/raw-pork-cut.jpg";
import rawChickenBreast from "assets/food-images/raw-chicken-breast.jpg";
import rawChickenWings from "assets/food-images/raw-chicken-wings.jpg";
import rawAsparagus from "assets/food-images/raw-asparagus.jpg";
import rawBroccoli from "assets/food-images/raw-broccoli.jpg";
import rawBrusselsSprouts from "assets/food-images/raw-brussels-sprouts.jpeg";
import crinkleCutFry from "assets/food-images/crinkle-fry.jpg";
import regularFry from "assets/food-images/regular-fry.jpg";
import steakFry from "assets/food-images/steak-fry.jpg";
import waffleFry from "assets/food-images/waffle-fry.jpg";
import pizzaRolls from "assets/food-images/pizza-rolls.jpg";

import foodItemIdToNameMapping from "../../config/foodItemIdToNameMapping.json";

function getFoodItemIdToNameMapping(foodItemId) {
  return foodItemIdToNameMapping[foodItemId];
}

function getImage(key) {
  const images = {
    "raw-steak": rawSteak,
    "raw-salmon": rawSalmon,
    "raw-pork-cut": rawPorkCut,
    "raw-chicken-breast": rawChickenBreast,
    "raw-chicken-wings": rawChickenWings,
    "raw-asparagus": rawAsparagus,
    "raw-broccoli": rawBroccoli,
    "raw-brussels-sprouts": rawBrusselsSprouts,
    "crinkle-fry": crinkleCutFry,
    "regular-fry": regularFry,
    "steak-fry": steakFry,
    "waffle-fry": waffleFry,
    "pizza-rolls": pizzaRolls,
  };

  return images[key];
}

function Tables() {
  const [historicalCookingSessions, setHistoricalCookingSessions] = useState([]);
  const [activeCookingSession, setActiveCookingSession] = useState(null);
  const [cookingSessionActive, setCookingSessionActive] = useState("");
  const [openHistoricalSessionId, setOpenHistoricalSessionId] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const [createdTime, setCreatedTime] = useState(0);
  const [endTime, setEndTime] = useState(0);

  const Author = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  async function fetchActiveCookingSessionData() {
    const sub = JSON.parse(localStorage.getItem("user")).sub;
    try {
      const response = await fetch(
        "https://int.api.chefaicookware.com/cooking-sessions-service/cooking-session/sub-status",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sub, status: "active" }),
        }
      );
      console.log("Fetching active cooking session data");
      if (response.ok) {
        const data = await response.json();
        // console.log(data);
        // Pick the first active cooking session and only render if endTime < now
        if (new Date() < new Date(data[0].endTime)) {
          setCreatedTime(data[0].startTime);
          setEndTime(data[0].endTime);
          setActiveCookingSession(data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function fetchHistorialCookingSessionData() {
    const sub = JSON.parse(localStorage.getItem("user")).sub;
    try {
      const response = await fetch(
        "https://int.api.chefaicookware.com/cooking-sessions-service/cooking-session/sub-status",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sub, status: "completed" }),
        }
      );
      console.log("Fetching historical cooking session data");
      if (response.ok) {
        const data = await response.json();
        // console.log(data);
        // Sort cooking sessions in chronological order
        data.sort((a, b) => {
          return new Date(b.startTime) - new Date(a.startTime);
        });
        // Take the latest cook and set the start and end times
        setCreatedTime(data[0].startTime);
        setEndTime(data[0].endTime);
        setHistoricalCookingSessions(data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    let interval1, interval2;
    try {
      fetchHistorialCookingSessionData();
      fetchActiveCookingSessionData();
      interval1 = setInterval(() => fetchHistorialCookingSessionData(), 5000); // 5 seconds
      interval2 = setInterval(() => fetchActiveCookingSessionData(), 5000); // 5 seconds
    } catch (error) {
      console.error("An error occurred:", error);
    }

    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, []);

  const mapCookingSessionToTableRow = () => {
    const cookingSessionRows = [];
    if (historicalCookingSessions && historicalCookingSessions.length > 0) {
      // Map data in chronological order
      historicalCookingSessions.map((session) => {
        cookingSessionRows.push({
          meal: (
            <Author
              image={getImage(getFoodItemIdToNameMapping(session.foodItemId))}
              name={undefined}
              email={undefined}
            />
          ),
          date: <Job title={new Date(session.startTime).toLocaleString()} />,
          status: (
            <MDBox ml={-1}>
              <MDBadge badgeContent="complete" color="success" variant="gradient" size="sm" />
            </MDBox>
          ),
          details: (
            <MDBox key={session.sessionId}>
              <div>
                <MDTypography
                  component="a"
                  href="#"
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                >
                  Food: {getFoodItemIdToNameMapping(session.foodItemId)}
                </MDTypography>
              </div>
              <div>
                <MDTypography
                  component="a"
                  href="#"
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                >
                  Cook Time: {Math.round(session.estimatedCookTime) / 60} minutes
                </MDTypography>
              </div>
              <div>
                <MDTypography
                  component="a"
                  href="#"
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                >
                  {`Cook Temperature ${Math.round(session.estimatedCookTemperature * 10) / 10}° C`}
                </MDTypography>
              </div>
              <MDButton
                onClick={() =>
                  openHistoricalSessionId === session.sessionId
                    ? handleSessionDetailsClose()
                    : handleSessionDetailsOpen(session.sessionId)
                }
                color="secondary"
                variant="text"
                startIcon={
                  openHistoricalSessionId === session.sessionId ? (
                    <RiArrowDropUpLine />
                  ) : (
                    <RiArrowDropDownLine />
                  )
                }
              >
                {openHistoricalSessionId === session.sessionId
                  ? "Hide Details"
                  : "Show More Details"}
              </MDButton>
              <Collapse in={openHistoricalSessionId === session.sessionId}>
                <Paper elevation={5}>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Shape: ${session.metadata.shape}`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Length: ${session.metadata.length} meters`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Width: ${session.metadata.width} meters`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Height: ${session.metadata.thickness} meters`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Weight: ${session.metadata.weight} kg`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Was Frozen: ${session.metadata.isFrozen}`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Initial Temperature: ${session.metadata.initialTemperature} ° C`}
                    </MDTypography>
                  </div>
                  <div>
                    <MDTypography
                      component="a"
                      href="#"
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      mt={3}
                      mb={3}
                      ml={3}
                      mr={3}
                    >
                      {`Target Temperature: ${session.metadata.targetTemperature}° C`}
                    </MDTypography>
                  </div>
                </Paper>
              </Collapse>
            </MDBox>
          ),
          action: session.feedbackSessionId ? (
            <MDButton variant="text" color="gold" disabled>
              Feedback
            </MDButton>
          ) : (
            <MDButton variant="gradient" color="gold" onClick={handleFeedbackOpen(session.id)}>
              Feedback
            </MDButton>
          ),
        });
      });
    }
    return {
      columns: [
        { Header: "Meal", accessor: "meal", width: "15%", align: "left" },
        { Header: "Date", accessor: "date", width: "10%", align: "left" },
        { Header: "Status", accessor: "status", width: "20%", align: "center" },
        { Header: "Details", accessor: "details", width: "20%", align: "center" },
        { Header: "Action", accessor: "action", width: "20%", align: "center" },
        { Header: "", accessor: "placeholder", width: "20%", align: "center" },
      ],

      rows: cookingSessionRows,
    };
  };

  const handleSessionDetailsOpen = (id) => {
    setOpenHistoricalSessionId(id);
  };

  const handleSessionDetailsClose = () => {
    setOpenHistoricalSessionId(null);
  };

  const handleFeedbackOpen = (id) => () => {
    setCookingSessionActive(id);
    setFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
  };

  const { columns, rows } = mapCookingSessionToTableRow();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="gold"
                borderRadius="lg"
                coloredShadow="gold"
              >
                <MDTypography variant="h5" color="white">
                  Current Cook
                </MDTypography>
              </MDBox>
              <MDBox py={2} px={60}>
                {activeCookingSession ? (
                  <>
                    <FadeIn delay={1000} transitionDuration={1500}>
                      <TimelineItem
                        color="info"
                        icon="play_arrow"
                        title={`Start cooking ${getFoodItemIdToNameMapping(
                          activeCookingSession[0].foodItemId
                        )}`}
                        dateTime={new Date(activeCookingSession[0].startTime).toLocaleString()}
                      />
                    </FadeIn>
                    <FadeIn delay={2000} transitionDuration={1500}>
                      <TimelineItem
                        color="warning"
                        icon="local_fire_department"
                        title={`Cooking at ${
                          Math.round(activeCookingSession[0].estimatedCookTemperature * 10) / 10
                        }° C`}
                        dateTime={new Date(activeCookingSession[0].startTime).toLocaleString()}
                      />
                    </FadeIn>
                    {/* In milliseconds. Don't need to divide */}
                    <FadeIn
                      delay={
                        new Date(activeCookingSession[0].endTime) -
                        new Date(activeCookingSession[0].startTime)
                      }
                      transitionDuration={1500}
                    >
                      <TimelineItem
                        color="success"
                        icon="done"
                        title={`Your ${getFoodItemIdToNameMapping(
                          activeCookingSession[0].foodItemId
                        )} is ready!`}
                        dateTime={new Date(activeCookingSession[0].endTime).toLocaleString()}
                        lastItem
                      />
                    </FadeIn>
                  </>
                ) : (
                  <></>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox pt={6} pb={3}>
        {feedbackOpen ? (
          <MDModal2
            open={feedbackOpen}
            handleClose={handleFeedbackClose}
            data={
              historicalCookingSessions.filter((session) => session.id === cookingSessionActive)[0]
            }
            fetchCallback={() => fetchData()}
          ></MDModal2>
        ) : (
          <></>
        )}
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="gold"
                borderRadius="lg"
                coloredShadow="gold"
              >
                <MDTypography variant="h5" color="white">
                  Cooking Session History
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Tables;
